import { useEffect, useState } from "react";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { AttachmentsInline } from "@/components/EmailComposer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Mail, MessageCircle, Save, Send, UserPlus, X } from "lucide-react";
import { normalizePhoneE164 } from "@/lib/utils";

const WA_NATION_LANG = { Italia: "it", Francia: "fr", Spagna: "es", Germania: "de", Austria: "de" };
const waLangForNation = (nation) => WA_NATION_LANG[(nation || "Italia").trim()] || "en";
const isValidEmail = (e) => !!e && /^\S+@\S+\.\S+$/.test(String(e).trim());
const phoneDigits = (p) => (p || "").replace(/\D/g, "");
const isMobile = (p) => {
  let d = phoneDigits(p);
  if (!d) return false;
  if (d.startsWith("00")) d = d.slice(2);
  if (d.startsWith("39") && d.length > 9) d = d.slice(2);
  return d.startsWith("3");
};
/** Numero WhatsApp utilizzabile: campo whatsapp/wa_number oppure telefono cellulare (stessa regola dei Rivenditori). */
export const contactWaNumber = (c) => c?.whatsapp || c?.wa_number || (c?.phone && isMobile(c.phone) ? c.phone : "");

const WaLangBadge = ({ nation, override }) => {
  const code = override && override !== "auto" ? override : waLangForNation(nation);
  return <span data-testid="recipient-lang-badge" className="inline-flex items-center rounded-full border border-border bg-brema-card px-1.5 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">{code}</span>;
};

const TARGET_CFG = {
  media: { idsKey: "media_ids", plural: "testate", single: "testata", allLabel: "tutte le testate selezionate", emailOf: (m) => m?.editor_email, module: "redazionali", fallbackName: "Redazione" },
  ambassadors: { idsKey: "ambassador_ids", plural: "ambassador", single: "ambassador", allLabel: "tutti gli ambassador selezionati", emailOf: (a) => a?.email, module: "ambassador", fallbackName: "Ambassador" },
};

/** Composer massivo condiviso (stesso motore /campaigns/bulk-send dei Rivenditori) per Testate e Ambassador. */
export function BulkCampaignComposer({ open, onOpenChange, channel, target, recipients, integrations, onSent, testidPrefix = "bulk" }) {
  const cfg = TARGET_CFG[target] || TARGET_CFG.media;
  const isWa = channel === "whatsapp";
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [ccOpen, setCcOpen] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [link, setLink] = useState("");
  const [lang, setLang] = useState("auto");
  const [waTplId, setWaTplId] = useState("none");
  const [waDoc, setWaDoc] = useState(null);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [waTemplates, setWaTemplates] = useState([]);
  const [assets, setAssets] = useState([]);
  const [extras, setExtras] = useState([]);
  const [newRecip, setNewRecip] = useState({ name: "", city: "", phone: "" });
  const [listOpen, setListOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [saveTplTitle, setSaveTplTitle] = useState("");
  const [saveTplSaving, setSaveTplSaving] = useState(false);

  const loadEmailTemplates = () => api.get(`/send-templates?module=${cfg.module}`).then(({ data }) => setEmailTemplates(data.templates || [])).catch(() => {});
  const loadWaTemplates = () => api.get("/message-templates?channel=whatsapp").then(({ data }) => setWaTemplates(data.templates || [])).catch(() => {});

  useEffect(() => {
    if (!open) return;
    setSubject(""); setBody(""); setCc(""); setBcc(""); setCcOpen(false); setAttachments([]);
    setLang("auto"); setWaTplId("none"); setWaDoc(null); setExtras([]); setNewRecip({ name: "", city: "", phone: "" }); setListOpen(false);
    setLink(integrations?.whatsapp_catalog_link || "");
    loadEmailTemplates(); loadWaTemplates();
    api.get("/marketing-assets").then(({ data }) => setAssets((data.assets || []).filter((a) => a.storage_path || a.file_path))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, channel, target]);

  const eligible = isWa ? recipients.filter((r) => !!contactWaNumber(r)) : recipients.filter((r) => isValidEmail(cfg.emailOf(r)));
  const skipped = recipients.length - eligible.length;
  const total = eligible.length + (isWa ? extras.length : 0);

  const applyEmailTemplate = (id) => {
    const t = emailTemplates.find((x) => x._id === id);
    if (t) { setSubject(t.subject || ""); setBody(t.body || ""); if (Array.isArray(t.attachments)) setAttachments(t.attachments); }
  };
  const applyWaTemplate = (id) => {
    setWaTplId(id);
    const t = waTemplates.find((x) => x._id === id);
    if (t) { setBody(t.preview_text || t.body || ""); if (t.default_link) setLink(t.default_link); }
  };
  const addExtra = () => {
    const norm = normalizePhoneE164(newRecip.phone, "Italia");
    if (!norm) { toast.error("Numero non valido: inserisci un telefono valido (es. +39 348 1234567)"); return; }
    if (extras.some((r) => r.phone === norm)) { toast.error("Numero già aggiunto"); return; }
    setExtras((arr) => [...arr, { name: (newRecip.name || "").trim() || cfg.fallbackName, city: (newRecip.city || "").trim(), phone: norm, nation: "Italia" }]);
    setNewRecip({ name: "", city: "", phone: "" });
  };
  const openConfirm = () => {
    if (!isWa && !body.trim()) { toast.error("Scrivi il testo del messaggio"); return; }
    if (total === 0) { toast.error(isWa ? "Nessun destinatario con numero WhatsApp valido" : "Nessun destinatario con email valida"); return; }
    setConfirmOpen(true);
  };
  const send = async () => {
    setSending(true);
    try {
      const tpl = waTemplates.find((t) => t._id === waTplId);
      const payload = isWa
        ? { target, [cfg.idsKey]: eligible.map((r) => r._id), channel: "whatsapp", wa_template: tpl?.meta_template_name || null, wa_preview: body || null, wa_document: waDoc, wa_link: link || null, wa_lang: lang === "auto" ? "" : lang, extra_recipients: extras }
        : { target, [cfg.idsKey]: eligible.map((r) => r._id), channel: "email", subject, body, cc, bcc, attachments };
      const { data } = await api.post("/campaigns/bulk-send", payload);
      const kind = isWa ? "numero WhatsApp" : "email valida";
      const sk = data.skipped || 0, errs = data.errors || 0;
      if ((data.sent || 0) === 0) toast.error(`Nessun messaggio inviato${errs ? ` · ${errs} falliti` : ""}${sk ? ` · ${sk} saltati (senza ${kind})` : ""}. Verifica recapiti e configurazione.`, { duration: 9000 });
      else if (errs > 0) toast.warning(`Inviati ${data.sent} · ${errs} NON inviati${sk ? ` · ${sk} saltati (senza ${kind})` : ""}.`, { duration: 9000 });
      else toast.success(`Campagna inviata a ${data.sent} destinatari${sk ? ` · ${sk} saltati (senza ${kind})` : ""}`);
      setConfirmOpen(false); onOpenChange(false); onSent?.(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invio fallito"); }
    finally { setSending(false); }
  };
  const saveTemplate = async () => {
    if (!saveTplTitle.trim()) { toast.error("Dai un titolo al modello"); return; }
    setSaveTplSaving(true);
    try {
      const tpl = waTemplates.find((t) => t._id === waTplId);
      await api.post("/message-templates", isWa
        ? { title: saveTplTitle, channel: "whatsapp", body, meta_template_name: tpl?.meta_template_name || "", default_link: link }
        : { title: saveTplTitle, channel: "email", subject, body, module: cfg.module, attachments });
      toast.success("Modello salvato in Libreria");
      setSaveTplOpen(false); setSaveTplTitle(""); loadEmailTemplates(); loadWaTemplates();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio fallito"); }
    finally { setSaveTplSaving(false); }
  };

  const accent = isWa ? "bg-[#25D366] text-white hover:bg-[#1FB855]" : "bg-primary text-primary-foreground hover:bg-primary/90";
  const T = (s) => `${testidPrefix}-${s}`;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto border-border bg-brema-card" data-testid={T("composer-dialog")}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              {isWa ? <MessageCircle className="h-5 w-5 text-[#25D366]" /> : <Mail className="h-5 w-5 text-brand" />}
              Campagna {isWa ? "WhatsApp" : "Email"} · {total} destinatari
            </DialogTitle>
            <DialogDescription>Invia un unico messaggio a {cfg.allLabel}: il segnaposto {"{nome}"} viene sostituito automaticamente.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              {isWa ? (
                <Select value={waTplId} onValueChange={applyWaTemplate}>
                  <SelectTrigger className="h-9 w-60 text-xs" data-testid={T("wa-template-select")}><SelectValue placeholder="Carica modello WhatsApp…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Modello: Standard</SelectItem>
                    {waTemplates.map((t) => <SelectItem key={t._id} value={t._id}>Modello: {t.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Select onValueChange={applyEmailTemplate}>
                  <SelectTrigger className="h-9 w-60 text-xs" data-testid={T("load-email-template")}><SelectValue placeholder="Carica da Archivio Modelli…" /></SelectTrigger>
                  <SelectContent>
                    {emailTemplates.length === 0 ? <SelectItem value="none" disabled>Nessun modello Email salvato</SelectItem>
                      : emailTemplates.map((t) => <SelectItem key={t._id} value={t._id}>{t.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Button variant="secondary" size="sm" data-testid={T("save-template-btn")} onClick={() => { setSaveTplTitle(""); setSaveTplOpen(true); }}
                className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover"><Save className="mr-1 h-3.5 w-3.5" /> Salva come Modello</Button>
            </div>
            {!isWa && (
              <div className="space-y-1.5">
                <Label>Oggetto Email</Label>
                <Input data-testid={T("subject-input")} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Es. Novità Brema Bike per {nome}" />
              </div>
            )}
            {!isWa && (!ccOpen ? (
              <button type="button" data-testid={T("ccbcc-toggle")} onClick={() => setCcOpen(true)} className="text-xs font-medium text-brand hover:underline">+ Aggiungi CC / CCN</button>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">CC (copia)</Label><Input data-testid={T("cc-input")} value={cc} onChange={(e) => setCc(e.target.value)} placeholder="collega@brema.it" /></div>
                <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">CCN (copia nascosta)</Label><Input data-testid={T("bcc-input")} value={bcc} onChange={(e) => setBcc(e.target.value)} placeholder="archivio@brema.it" /></div>
              </div>
            ))}
            <div className="space-y-1.5">
              <Label>{isWa ? "Testo messaggio (anteprima template)" : "Testo Email"}</Label>
              <Textarea data-testid={T("body-input")} value={body} onChange={(e) => setBody(e.target.value)} className="min-h-[150px]"
                placeholder={isWa ? "Ciao {nome}! Scopri le novità Brema: {link}" : "Gentile {nome},\nsiamo lieti di presentarvi la collezione Brema Bike…"} />
              <p className="text-xs text-muted-foreground" data-testid={T("vars-hint")}>
                Variabili: <code className="rounded bg-muted px-1">{"{nome}"}</code> <code className="rounded bg-muted px-1">{"{regione}"}</code> <code className="rounded bg-muted px-1">{"{categoria}"}</code> <code className="rounded bg-muted px-1">{"{link_press_lounge}"}</code> <code className="rounded bg-muted px-1">{"{link_ambassador_lounge}"}</code>{isWa ? <> <code className="rounded bg-muted px-1">{"{link}"}</code></> : null}
              </p>
            </div>
            {!isWa && (
              <div className="space-y-1.5">
                <Label>Allegati</Label>
                <AttachmentsInline attachments={attachments} onAdd={(r) => setAttachments((p) => [...p, r])} onRemove={(sp) => setAttachments((p) => p.filter((a) => a.storage_path !== sp))} testidPrefix={testidPrefix} />
              </div>
            )}
            {isWa && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label>Link ({"{link}"})</Label>
                  <Input data-testid={T("wa-link-input")} value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…" />
                </div>
                <div className="space-y-1.5">
                  <Label>Allega documento</Label>
                  <Select value={waDoc?.storage_path || "none"} onValueChange={(v) => {
                    if (v === "none") { setWaDoc(null); return; }
                    const a = assets.find((x) => (x.storage_path || x.file_path) === v);
                    if (a) setWaDoc({ storage_path: v, link: fileUrl(v), filename: a.original_filename || a.title || a.name || "Documento.pdf" });
                  }}>
                    <SelectTrigger className="h-9 text-xs" data-testid={T("wa-doc-select")}><SelectValue placeholder="Allega…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessun allegato</SelectItem>
                      {assets.map((a) => { const sp = a.storage_path || a.file_path; return <SelectItem key={sp} value={sp}>{a.original_filename || a.title || a.name || "File"}</SelectItem>; })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Lingua template</Label>
                  <Select value={lang} onValueChange={setLang}>
                    <SelectTrigger className="h-9 text-xs" data-testid={T("wa-lang-select")}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Automatica (paese)</SelectItem>
                      <SelectItem value="it">Italiano (it)</SelectItem>
                      <SelectItem value="fr">Français (fr)</SelectItem>
                      <SelectItem value="es">Español (es)</SelectItem>
                      <SelectItem value="de">Deutsch (de)</SelectItem>
                      <SelectItem value="en">English (en)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            {isWa && (
              <div className="rounded-lg border border-dashed border-border bg-brema-bg/50 p-3" data-testid={T("add-recipient-box")}>
                <Label className="text-xs font-semibold text-foreground">Aggiungi destinatario (solo per questa campagna, non salvato in archivio)</Label>
                <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_1.2fr_auto]">
                  <Input data-testid={T("new-recip-name")} value={newRecip.name} onChange={(e) => setNewRecip((p) => ({ ...p, name: e.target.value }))} placeholder="Nome" className="h-9 text-xs" />
                  <Input data-testid={T("new-recip-city")} value={newRecip.city} onChange={(e) => setNewRecip((p) => ({ ...p, city: e.target.value }))} placeholder="Città" className="h-9 text-xs" />
                  <Input data-testid={T("new-recip-phone")} value={newRecip.phone} onChange={(e) => setNewRecip((p) => ({ ...p, phone: e.target.value }))}
                    onBlur={() => { const n = normalizePhoneE164(newRecip.phone, "Italia"); if (n) setNewRecip((p) => ({ ...p, phone: n })); }} placeholder="+39 348 1234567" className="h-9 text-xs" />
                  <Button type="button" data-testid={T("add-recip-btn")} onClick={addExtra} className="h-9 rounded-full bg-[#25D366] px-3 text-xs font-semibold text-white hover:bg-[#1FB855]"><UserPlus className="mr-1 h-3.5 w-3.5" /> Aggiungi</Button>
                </div>
                {extras.length > 0 && (
                  <ul className="mt-2 space-y-1" data-testid={T("extra-recipients-list")}>
                    {extras.map((r) => (
                      <li key={r.phone} className="flex items-center justify-between gap-2 rounded border border-border/60 bg-brema-card px-2 py-1 text-xs" data-testid={T("extra-recipient-row")}>
                        <span className="flex items-center gap-1.5 truncate text-foreground"><WaLangBadge nation={r.nation} override={lang} /><b>{r.name}</b>{r.city ? ` · ${r.city}` : ""} · {r.phone}</span>
                        <button type="button" onClick={() => setExtras((arr) => arr.filter((x) => x.phone !== r.phone))} data-testid={T("remove-recip-btn")} className="shrink-0 text-muted-foreground hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {isWa && !integrations?.whatsapp && <p className="text-xs text-red-500" data-testid={T("wa-simulated-note")}>Invio simulato: i secret WhatsApp non sono ancora configurati.</p>}

            <div className="rounded-lg border border-border bg-brema-bg p-3" data-testid={T("recipients-summary")}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-foreground" data-testid={T("recipients-count")}>
                  {total} {total === 1 ? "destinatario riceverà" : "destinatari riceveranno"} il messaggio{isWa && extras.length > 0 ? ` (${eligible.length} dall'archivio + ${extras.length} aggiunti)` : ""}
                </p>
                {eligible.length > 0 && <button type="button" data-testid={T("recipients-toggle")} onClick={() => setListOpen((o) => !o)} className="text-xs font-medium text-brand hover:underline">{listOpen ? "Nascondi elenco" : "Mostra elenco"}</button>}
              </div>
              {skipped > 0 && <p className="mt-1 text-xs text-amber-600 dark:text-amber-400" data-testid={T("recipients-skipped")}>{skipped} {skipped === 1 ? "contatto selezionato verrà saltato" : "contatti selezionati verranno saltati"} (senza {isWa ? "numero WhatsApp" : "email valida"}).</p>}
              {listOpen && eligible.length > 0 && (
                <ul className="mt-2 max-h-44 space-y-1 overflow-y-auto pr-1" data-testid={T("recipients-list")}>
                  {eligible.map((r) => (
                    <li key={r._id} className="flex items-center justify-between gap-2 border-b border-border/50 py-1 text-xs" data-testid={T("recipient-row")}>
                      <span className="truncate font-medium text-foreground">{r.name}</span>
                      <span className="flex items-center gap-1.5 truncate text-muted-foreground">{isWa && <WaLangBadge nation={r.nation} override={lang} />}{isWa ? contactWaNumber(r) : cfg.emailOf(r)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="secondary" onClick={() => onOpenChange(false)} className="rounded-full border border-border bg-transparent text-foreground hover:bg-brema-hover">Annulla</Button>
              <Button data-testid={T("send-btn")} onClick={openConfirm} disabled={sending || total === 0} className={`rounded-full font-semibold disabled:opacity-40 ${accent}`}>
                <Send className="mr-2 h-4 w-4" /> Invia Campagna a {total} {total === 1 ? "Destinatario" : "Destinatari"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md border-border bg-brema-card" data-testid={T("confirm-dialog")}>
          <DialogHeader>
            <DialogTitle className="text-foreground">Conferma invio campagna</DialogTitle>
            <DialogDescription>Sei sicuro di voler inviare questo messaggio a <span className="font-semibold text-foreground">{total}</span> {total === 1 ? "destinatario" : "destinatari"} via {isWa ? "WhatsApp" : "Email"}? L'operazione è immediata e non annullabile.</DialogDescription>
          </DialogHeader>
          {skipped > 0 && <p className="text-xs text-amber-600 dark:text-amber-400">{skipped} contatti selezionati senza {isWa ? "numero" : "email"} verranno saltati.</p>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" data-testid={T("confirm-cancel")} onClick={() => setConfirmOpen(false)} className="rounded-full border border-border bg-transparent text-foreground hover:bg-brema-hover">Annulla</Button>
            <Button data-testid={T("confirm-send")} onClick={send} disabled={sending} className={`rounded-full font-semibold ${accent}`}>
              {sending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Invio…</> : <><Send className="mr-2 h-4 w-4" /> Conferma e invia</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={saveTplOpen} onOpenChange={setSaveTplOpen}>
        <DialogContent className="max-w-md border-border bg-brema-card" data-testid={T("save-template-dialog")}>
          <DialogHeader>
            <DialogTitle className="text-foreground">Salva nella Libreria Modelli</DialogTitle>
            <DialogDescription>Il testo attuale sarà salvato come modello {isWa ? "WhatsApp" : "Email"} riutilizzabile.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Titolo del modello *</Label>
            <Input data-testid={T("save-template-title")} value={saveTplTitle} onChange={(e) => setSaveTplTitle(e.target.value)} placeholder="Es. Presentazione collezione 2026" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setSaveTplOpen(false)} className="rounded-full border border-border bg-transparent text-foreground hover:bg-brema-hover">Annulla</Button>
            <Button data-testid={T("save-template-confirm")} onClick={saveTemplate} disabled={saveTplSaving} className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
              {saveTplSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva modello"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Toggle canale Email / WhatsApp (stesso aspetto dei Rivenditori). */
export function ChannelToggle({ value, onChange, testidPrefix = "bulk" }) {
  return (
    <div className="inline-flex overflow-hidden rounded-full border border-border" data-testid={`${testidPrefix}-channel-toggle`}>
      <button type="button" data-testid={`${testidPrefix}-channel-email`} onClick={() => onChange("email")}
        className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium transition-colors ${value === "email" ? "bg-selected text-selected-foreground" : "bg-transparent text-muted-foreground hover:bg-brema-hover"}`}>
        <Mail className="h-3.5 w-3.5" /> E-mail
      </button>
      <button type="button" data-testid={`${testidPrefix}-channel-whatsapp`} onClick={() => onChange("whatsapp")}
        className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium transition-colors ${value === "whatsapp" ? "bg-positive text-white" : "bg-transparent text-muted-foreground hover:bg-brema-hover"}`}>
        <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
      </button>
    </div>
  );
}
